<template>
  <Form
    v-bind="getBindValue"
    ref="formElRef"
    :class="getFormClass"
    :model="formModel"
    @keydown.enter="handleKeyboardEnter"
  >
    <Row v-bind="getRow">
      <slot name="formHeader"></slot>
      <template v-for="schema in getQuickSchema" :key="schema['_key'] || schema.field">
        <FormItem
          :tableAction="tableAction"
          :formActionType="formActionType"
          :schema="schema"
          :formProps="getProps"
          :allDefaultValues="defaultValueRef"
          :formModel="formModel"
          :setFormModel="setFormModel"
        >
          <template v-for="item in Object.keys($slots)" #[item]="data">
            <slot :name="item" v-bind="data || {}"></slot>
          </template>
        </FormItem>
      </template>

      <FormAction v-bind="getFormActionBindProps" @open-more="handleOpenMore">
        <template
          v-for="item in ['resetBefore', 'submitBefore', 'advanceBefore', 'advanceAfter']"
          #[item]="data"
        >
          <slot :name="item" v-bind="data || {}"></slot>
        </template>
      </FormAction>
      <slot name="formFooter"></slot>
    </Row>
  </Form>

  <div ref="searchModal"> </div>
  <BasicModal
    :getContainer="() => $refs.searchModal"
    v-bind="getModalProps"
    class="mts-search-modal"
    @register="registerModal"
    @ok="onModalOk"
  >
    <template v-if="PCMode" #title>
      <ModalHeader :title="getModalProps.title" class="!inline-flex" />
      <SyncOutlined
        v-if="formCache"
        class="ml-6px p-4px"
        :title="t('g.26010002')"
        @click="formCache.clear"
      />
    </template>
    <Form
      v-bind="getModalFormBindValue"
      ref="modalFormElRef"
      :class="[prefixCls, 'mts-search-modal-form']"
      @keydown.enter="handleKeyboardEnter"
    >
      <Row v-bind="getModalFormRow">
        <template v-for="schema in getModalFormSchema" :key="schema['_key'] || schema.field">
          <FormItem
            :tableAction="tableAction"
            :formActionType="formActionType"
            :schema="schema"
            :formProps="{ ...getProps, ...getModalFormProps }"
            :allDefaultValues="defaultValueRef"
            :formModel="formModel"
            :setFormModel="setFormModel"
            :togglePin="togglePin"
          >
            <template v-for="item in Object.keys($slots)" #[item]="data">
              <slot :name="item" v-bind="data || {}"></slot>
            </template>
          </FormItem>
        </template>
      </Row>
    </Form>
  </BasicModal>
</template>

<script lang="ts" setup>
  import { SyncOutlined } from '@ant-design/icons-vue';
  import { Form, type FormProps as AntFormProps, Row } from 'ant-design-vue';
  import { findLastIndex, sum } from 'lodash-es';
  import {
    computed,
    nextTick,
    onMounted,
    reactive,
    ref,
    shallowRef,
    unref,
    useAttrs,
    watch,
  } from 'vue';
  import type { Ref } from 'vue';
  import {
    cloneDeep,
    dateUtil,
    deepMerge,
    getFormConfig,
    isArray,
    isFunction,
    message,
    omit,
  } from '@mts-fe/utils';
  import { BasicModal, useModal, useModalContext } from '@mts/components/Modal';
  import ModalHeader from '@mts/components/Modal/src/components/ModalHeader.vue';
  import { TableActionType } from '@mts/components/Table';
  import { useDesign } from '@mts/hooks/web/useDesign';
  import { t } from '@mts/hooks/web/useI18n';
  import { getGlobalParams } from '@mts/render';
  import { getModalSize } from '@mts/utils';
  import { PCMode } from '@mts/utils/env';
  import FormAction from './components/FormAction.vue';
  import FormItem from './components/FormItem.vue';
  import { createFormContext } from './hooks/useFormContext';
  import { useFormEvents } from './hooks/useFormEvents';
  import { useFormFocus } from './hooks/useFormFocus';
  import { useFormValues } from './hooks/useFormValues';
  import type { FormActionType, FormProps, FormSchemaInner as FormSchema } from './types/form';
  import type { AdvanceState } from './types/hooks';
  import { createFormCache } from './cache';
  import {
    dateItemType,
    getReadonlyComponent,
    IGNORE_PROPS_KEYS,
    isIncludeSimpleComponents,
    itemIsUploadComponent,
  } from './helper';
  import { basicProps } from './props';

  defineOptions({ name: 'BasicForm', inheritAttrs: false });

  const props = defineProps(basicProps);

  const emit = defineEmits([
    'advanced-change',
    'reset',
    'submit',
    'register',
    'focus-change',
    'field-value-change',
    'change',
  ]);

  const attrs = useAttrs();

  const formModel = reactive({});
  const modalFn = useModalContext();

  const advanceState = reactive<AdvanceState>({
    isAdvanced: true,
    hideAdvanceBtn: true,
    isLoad: false,
    actionSpan: Number(props.actionColOptions?.span) || 6,
  });

  const defaultValueRef = ref({});
  const isInitedDefaultRef = ref(false);
  const propsRef = ref<Partial<FormProps>>();
  const schemaRef = ref<FormSchema[] | null>(null);
  const formElRef = ref<FormActionType | null>(null);
  const modalFormElRef = shallowRef<FormActionType | null>(null);

  const { prefixCls } = useDesign('basic-form');

  // Get the basic configuration of the form
  const getProps = computed(() => {
    return { ...props, ...unref(propsRef) } as FormProps;
  });

  const getFormClass = computed(() => {
    const { inset, readonlyMode, compact } = unref(getProps);
    return [
      prefixCls,
      {
        [`${prefixCls}--inset`]: inset,
        [`${prefixCls}--readonly`]: readonlyMode,
        [`${prefixCls}--compact`]: compact,
      },
    ];
  });

  // Get uniform row style and Row configuration for the entire form
  const getRow = computed(() => {
    const { baseRowStyle = {}, rowProps } = unref(getProps);
    return {
      style: baseRowStyle,
      ...rowProps,
    };
  });

  const getBindValue = computed(() =>
    omit({ ...attrs, ...props, ...unref(getProps) } as AntFormProps, IGNORE_PROPS_KEYS),
  );

  const globalParams = getGlobalParams();
  const renderKeys = shallowRef<Recordable<string>>({});
  const getSchema = computed((): FormSchema[] => {
    const schemas: (FormSchema & { ifshow2?: boolean })[] = cloneDeep(
      unref(schemaRef) || (unref(getProps).schemas as any),
    );

    const { readonlyMode, layout, hideLabel } = unref(getProps);
    for (const schema of schemas) {
      const key = renderKeys.value[schema.field];
      if (key) {
        schema['_key'] = key;
      }
      if (readonlyMode) {
        schema.component = getReadonlyComponent(schema.component, schema.componentProps.longText);
        schema.required = false;
      }
      if (layout !== 'inline' && schema.showIn === 'inlineText') {
        Object.assign(schema, {
          colProps: { ...schema.colProps, span: 'unset', class: 'mts-col-inline' },
        });
      }

      const {
        defaultValue,
        component,
        componentProps = {},
        isHandleDateDefaultValue = true,
        field,
        isHandleDefaultValue = true,
        valueFormat,
        ifShow,
      } = schema;

      //fix:修复showAdvancedButton为true时，FormSchema中ifshow是与model有关的函数时候，查询按钮位置不重新计算的问题。
      if (unref(getProps).showAdvancedButton) {
        schema.ifshow2 = isFunction(ifShow)
          ? ifShow({
              schema,
              values: formModel,
              model: formModel,
              field,
              ...globalParams,
            })
          : ifShow;
      }
      // label支持多语言
      if (schema.label && typeof schema.label === 'string') {
        schema.dataLangKey =
          typeof schema.label === 'string' && t(schema.label) !== schema.label ? schema.label : '';
        schema.label = t(schema.label);
      }
      if (hideLabel) {
        schema.hideLabel = schema.hideLabel ?? true;
      }

      // handle date type
      if (
        isHandleDateDefaultValue &&
        defaultValue &&
        component &&
        dateItemType.includes(component)
      ) {
        const opt = {
          schema,
          tableAction: props.tableAction ?? ({} as TableActionType),
          formModel,
          formActionType: {} as FormActionType,
        };
        const valueFormat = componentProps
          ? typeof componentProps === 'function'
            ? componentProps(opt)['valueFormat']
            : componentProps['valueFormat']
          : null;
        if (!isArray(defaultValue)) {
          schema.defaultValue = valueFormat
            ? dateUtil(defaultValue).format(valueFormat)
            : dateUtil(defaultValue);
        } else {
          const def: any[] = [];
          defaultValue.forEach((item) => {
            def.push(valueFormat ? dateUtil(item).format(valueFormat) : dateUtil(item));
          });
          schema.defaultValue = def;
        }
      }
      // handle upload type
      if (defaultValue && itemIsUploadComponent(schema?.component)) {
        if (isArray(defaultValue)) {
          schema.defaultValue = defaultValue;
        } else if (typeof defaultValue == 'string') {
          schema.defaultValue = [defaultValue];
        }
      }

      // handle schema.valueFormat
      if (isHandleDefaultValue && defaultValue && component && isFunction(valueFormat)) {
        schema.defaultValue = valueFormat({
          value: defaultValue,
          schema,
          model: formModel,
          field,
        });
      }
    }
    if (unref(getProps).showAdvancedButton) {
      return schemas.filter(
        (schema) => !isIncludeSimpleComponents(schema.component),
      ) as FormSchema[];
    }

    const sortedSchemas = [
      ...schemas.filter((i) => i.showIn === 'inlineText'),
      ...schemas.filter((i) => i.showIn !== 'inlineText'),
    ];
    const lastIndex = findLastIndex(sortedSchemas, { showIn: 'inlineText' });
    if (lastIndex !== -1) {
      sortedSchemas.splice(lastIndex + 1, 0, { field: '_BR_' });
    }
    return sortedSchemas as FormSchema[];
  });

  const getQuickSchema = computed(() =>
    cloneDeep(unref(getSchema)).filter((schema) => !schema.hideInMore),
  );

  const { handleFormValues, initDefault, updateFormModel } = useFormValues({
    getProps,
    defaultValueRef,
    getSchema,
    formModel,
  });

  const emitChange = (schema: FormSchema) =>
    (PCMode || (!PCMode && !isModalOpen?.value)) && emit('change', { ...formModel }, schema);

  const formAction = useFormEvents({
    emit,
    getProps,
    formModel,
    getSchema,
    defaultValueRef,
    formElRef: formElRef as Ref<FormActionType>,
    schemaRef: schemaRef as Ref<FormSchema[]>,
    handleFormValues,
    updateFormModel,
    emitChange,
    renderKeys,
  });

  const { handleKeyboardEnter, focusByField } = useFormFocus({
    getFormEl: () => (isModalOpen?.value ? modalFormElRef?.value?.$el : formElRef.value?.$el),
    getProps,
    handleSubmit: formAction.submit,
    setFieldsValue: formAction.setFieldsValue,
    emit,
  });

  createFormContext({
    resetAction: formAction.resetFields,
    submitAction: formAction.submit,
  });

  watch(
    () => unref(getProps).model,
    () => {
      const { model } = unref(getProps);
      if (!model) return;
      formAction.setFieldsValue(model);
    },
    {
      immediate: true,
    },
  );

  watch(
    () => props.schemas,
    (schemas) => {
      formAction.resetSchema(schemas ?? []);
    },
  );

  watch(
    () => getSchema.value,
    (schema) => {
      nextTick(() => {
        //  Solve the problem of modal adaptive height calculation when the form is placed in the modal
        modalFn?.redoModalHeight?.();
      });
      if (unref(isInitedDefaultRef)) {
        return;
      }
      if (schema?.length) {
        initDefault(formAction.updateRelateFields);
        isInitedDefaultRef.value = true;
      }
    },
  );

  async function setProps(formProps: Partial<FormProps>): Promise<void> {
    propsRef.value = deepMerge(unref(propsRef) || {}, formProps);

    if (formProps.schemas?.length && schemaRef.value?.length)
      formAction.resetSchema(formProps.schemas);
  }

  function setFormModel(key: string, value: any, schema: FormSchema) {
    formModel[key] = value;
    emit('field-value-change', key, value, schema);
    emitChange(schema);

    updateFormModel(key, schema);
    formAction.updateRelateFields(key, value, schema);
    // TODO 优化验证，这里如果是autoLink=false手动关联的情况下才会再次触发此函数
    if (schema && schema.itemProps && !schema.itemProps.autoLink) {
      formAction.validateFields([key]).catch((_) => {});
    }

    if (!isModalOpen?.value && schema.triggerSearch && getProps.value.autoSubmitOnEnter) {
      formAction.submit();
    }
  }

  const getFormActionBindProps = computed(
    () => ({ ...getProps.value, ...advanceState }) as InstanceType<typeof FormAction>['$props'],
  );

  const formActionType = { ...formAction, setProps, focusByField };
  defineExpose(formActionType);

  const formCache = createFormCache(getBindValue.value.name);
  onMounted(() => {
    initDefault(formAction.updateRelateFields);
    formCache?.restore(formAction.updateSchema);
    emit('register', formActionType);
  });

  const getModalProps = computed(() =>
    deepMerge(
      {
        title: t(props.moreButtonOptions?.text || 'common.moreFilters'),
        ...getModalSize('middle', false),
        okText: props.submitButtonOptions?.text || 'common.queryText',
        okButtonProps: props.submitButtonOptions,
        canFullscreen: !PCMode,
      },
      props.moreModalProps,
    ),
  );
  const getModalFormProps = computed(() =>
    getFormConfig({ name: getBindValue.value.name + '_FILTER' }),
  );
  const getModalFormBindValue = computed(() =>
    deepMerge(getBindValue.value, getModalFormProps.value),
  );
  const getModalFormRow = computed(() => {
    const { baseRowStyle = {}, rowProps } = unref(getModalFormBindValue as FormProps);
    return { style: baseRowStyle, ...rowProps };
  });
  const actionColSpan = computed(() => {
    const span = getProps.value.actionColOptions?.span ?? 4;
    return typeof span === 'number' ? span : Number(span);
  });
  const getColSpan = (schema?: FormSchema) => {
    const span = schema?.colProps?.span ?? actionColSpan.value;
    return typeof span === 'number' ? span : Number(span);
  };
  const getModalFormSchema = computed(() =>
    cloneDeep(unref(getSchema)).map((schema) => {
      const span = getColSpan(schema);
      return deepMerge(schema, {
        colProps: { span: 2 * (PCMode ? span : span > 6 ? 12 : 6) },
        hideLabel: false,
      });
    }),
  );
  const [registerModal, { openModal, getOpen: isModalOpen }] = useModal();
  const handleOpenMore = () => {
    openModal(true);
  };
  const togglePin = (field: string, hideInMore: boolean) => {
    const schemas = cloneDeep(unref(getSchema));
    if (
      hideInMore ||
      sum(schemas.filter((schema) => !schema.hideInMore).map(getColSpan)) +
        getColSpan(schemas.find((schema) => schema.field === field)) <=
        24 - actionColSpan.value
    ) {
      formAction.updateSchema({ field, hideInMore });
      formCache?.savePin({ field, hideInMore });
    } else {
      message.warn(t('g.25090010'));
    }
  };
  const onModalOk = () => {
    formAction.submit();
    openModal(false);
  };
</script>
<style lang="less">
  @prefix-cls: ~'@{namespace}-basic-form';

  .@{prefix-cls} {
    &.ant-form {
      padding: 6px 16px;
      line-height: @line-height;

      .ant-form-item {
        margin-bottom: 8px;
        line-height: @line-height;

        .text-secondary {
          color: rgb(0 0 0 / 45%);
        }

        &-label > label {
          color: #8991a1;

          &::after {
            margin-right: 6px;
          }
        }

        &.label-hidden label {
          visibility: hidden;
        }

        // &-with-help {
        //   margin-bottom: 0;
        // }

        // &:not(.ant-form-item-with-help) {
        //   margin-bottom: 20px;
        // }

        &.suffix-item,
        &.prefix-item {
          .ant-form-item-children {
            display: flex;
          }

          .prefix {
            display: inline-flex;
            align-items: center;
            padding-right: 6px;
          }

          .suffix {
            display: inline-flex;
            align-items: center;
            padding-left: 6px;
          }
        }
      }

      .ant-form-explain {
        font-size: 14px;
      }

      &-vertical .ant-form-item-label {
        padding-bottom: 4px;
      }

      &-vertical .ant-form-item-explain-error {
        position: absolute;
        top: -26px;
        right: 0;
        background-color: #fff;
      }

      &-inline .ant-form-item {
        margin-right: 0;
      }

      .ant-col.mts-col-inline {
        padding-bottom: 4px;

        .ant-form-item-row {
          flex-direction: row;
        }

        .ant-form-item-label,
        .ant-form-item-control {
          width: unset !important;
        }

        .ant-form-item-label > label::after {
          display: block;
        }
      }
    }

    &.@{prefix-cls}--compact .ant-form-item {
      margin-bottom: 0;
    }

    &.control-height-unset .ant-form-item-control-input {
      min-height: unset;
    }

    &--inset {
      padding: 0 !important;
    }

    &.mts-form-bg.ant-form {
      display: inline-flex;
      border-radius: 4px;
      background-color: #ecf1fe;
    }

    &--readonly.ant-form,
    .mts-col-inline {
      .ant-form-item {
        &-label {
          padding-bottom: 0;

          > label {
            height: unset;
          }
        }

        &-control-input {
          min-height: unset;
          padding-right: 4px;
        }
      }
    }

    &.form-row-truncate {
      position: relative;
      overflow: hidden;

      .ant-row {
        flex-shrink: 0;
      }

      &.max-row-2 {
        max-height: 42px;

        .ant-row {
          flex-shrink: unset;
        }
      }
    }
  }

  .mts-pc {
    .@{prefix-cls}--readonly:not(.mts-form-bg).ant-form {
      padding-top: 0;
      padding-bottom: 0;
    }
  }

  .mts-pad {
    .ant-form {
      padding-right: 8px;
      padding-left: 8px;

      /* contain: layout; */
    }
  }

  .mts-search-modal {
    .mts-search-modal-form {
      padding-bottom: 8px;
    }

    .ant-form.ant-form-vertical .ant-form-item-label > label {
      display: block;

      .mts-search-pin {
        display: block;
        float: right;
        color: #bcc3ce;
        line-height: 1.3;
        cursor: pointer;
      }
    }
  }
</style>
